import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { formatLongDate } from "@/lib/date";
import { vehicleTitle } from "@/lib/format";
import type { BookingWithVehicle } from "@/types";

interface ReviewModalProps {
  booking: BookingWithVehicle;
  onClose: () => void;
  onSubmitted: (bookingId: number) => void;
}

export default function ReviewModal({ booking, onClose, onSubmitted }: ReviewModalProps) {
  const { user, profile } = useAuth();
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "error">("idle");
  const [message, setMessage] = useState("");

  const title = booking.vehicle
    ? vehicleTitle(booking.vehicle.make, booking.vehicle.model, booking.vehicle.year)
    : "Your Gari car";

  async function handleSubmit() {
    if (!user) return;
    if (comment.trim().length < 5) {
      setStatus("error");
      setMessage("Add a few words about your trip so other riders can learn from it.");
      return;
    }

    setStatus("saving");
    setMessage("");
    try {
      const { error } = await supabase.from("reviews").insert({
        vehicle_id: booking.vehicle_id,
        booking_id: booking.id,
        reviewer_id: user.id,
        reviewer_name: profile?.full_name || user.email || "Gari rider",
        reviewer_avatar: profile?.avatar_url || null,
        rating,
        comment: comment.trim(),
      });
      if (error) throw error;
      onSubmitted(booking.id);
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "We couldn't post your review. Please try again.");
    }
  }

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-t-2xl border border-background-200 bg-background-50 sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-background-200/70 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-foreground-950">Leave a review</h2>
            <p className="mt-1 text-sm text-foreground-600">
              {title} · {formatLongDate(booking.start_date)} → {formatLongDate(booking.end_date)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close review"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-background-200 text-lg text-foreground-600 transition-colors hover:text-foreground-950"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-foreground-500">Your rating</p>
          <div className="mt-3 flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHover(star)}
                onMouseLeave={() => setHover(0)}
                aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                className="text-3xl transition-transform hover:scale-110"
              >
                <i className={`${star <= (hover || rating) ? "ri-star-fill text-primary-500" : "ri-star-line text-foreground-400"}`}></i>
              </button>
            ))}
            <span className="ml-2 font-heading text-lg font-semibold text-foreground-950">{rating}.0</span>
          </div>

          <div className="mt-5">
            <label htmlFor="review-comment" className="text-xs font-medium uppercase tracking-wider text-foreground-500">
              Tell us about the trip
            </label>
            <textarea
              id="review-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 500))}
              rows={4}
              maxLength={500}
              placeholder="How was the car? How was the host and the handover?"
              className="mt-2 w-full resize-none rounded-lg border border-background-200/70 bg-background-100 px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
            />
            <p className="mt-1 text-right text-xs text-foreground-500">{comment.length}/500</p>
          </div>

          {status === "error" && message && (
            <p className="mt-4 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-3 text-sm text-secondary-300">
              <i className="ri-error-warning-line"></i> {message}
            </p>
          )}

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={status === "saving"}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50"
            >
              {status === "saving" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> Posting
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill"></i> Post review
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-5 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}